import { useState, useCallback } from "react";

import axios from "axios";

import applicationApi from "@domains/application/module/api/applicationApi";
import { ApplicationEndpoints } from "@generated/application";
import { Application, StackStatus } from "src/domains/application/types";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

interface UseStackActionsParams {
  sysAlias: string | undefined;
  orgAlias: string | undefined;
  projectAlias: string | undefined;
  system: Application | null;
  setSystem: React.Dispatch<React.SetStateAction<Application | null>>;
}

interface UseStackActionsReturn {
  // Shared loading / error state
  actionLoading: boolean;
  actionError: string | null;
  clearActionError: () => void;
  deployMessage: string | null;
  clearDeployMessage: () => void;

  // Individual loading flags for non-actionLoading flows
  retryProvisioningLoading: boolean;
  deleteProtectionLoading: boolean;
  autoDeployLoading: boolean;
  promoteLoading: boolean;

  // Dialog state
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;

  // Action handlers
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  deploy: (targetEnvironmentSlug: string) => Promise<void>;
  buildAndDeploy: (targetEnvironmentSlug: string) => Promise<void>;
  retryRun: (sagaId: string) => Promise<void>;
  provisionVcs: () => Promise<void>;
  retryProvisioning: () => Promise<void>;
  softDelete: () => Promise<void>;
  hardDelete: () => Promise<void>;
  deleteProtectionChange: (enabled: boolean) => Promise<void>;
  autoDeployChange: (enabled: boolean) => Promise<void>;
  promote: (targetSlug: string) => Promise<void>;
}

const authHeader = () => ({
  withCredentials: true as const,
});

export function useStackActions({
  sysAlias,
  orgAlias,
  projectAlias,
  system,
  setSystem,
}: UseStackActionsParams): UseStackActionsReturn {
  const nav = useAppNavigation();

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deployMessage, setDeployMessage] = useState<string | null>(null);

  const [retryProvisioningLoading, setRetryProvisioningLoading] =
    useState(false);
  const [deleteProtectionLoading, setDeleteProtectionLoading] = useState(false);
  const [autoDeployLoading, setAutoDeployLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const clearActionError = useCallback(() => setActionError(null), []);
  const clearDeployMessage = useCallback(() => setDeployMessage(null), []);

  const pause = useCallback(async () => {
    if (!sysAlias || !system) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.PAUSE_APPLICATION.replace(":id", sysAlias)}`,
        {},
        { ...authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.STOPPED } : s));
    } catch {
      setActionError("Failed to pause stack. Please try again.");
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, system, setSystem]);

  const resume = useCallback(async () => {
    if (!sysAlias || !system) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.RESUME_APPLICATION.replace(":id", sysAlias)}`,
        {},
        { ...authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.RUNNING } : s));
    } catch {
      setActionError("Failed to resume stack. Please try again.");
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, system, setSystem]);

  const deploy = useCallback(
    async (targetEnvironmentSlug: string) => {
      if (!sysAlias || !targetEnvironmentSlug) return;
      setActionLoading(true);
      setActionError(null);
      setDeployMessage(null);
      try {
        await applicationApi.deployOnly(
          sysAlias,
          undefined,
          targetEnvironmentSlug,
        );
        setDeployMessage("Deploy triggered — stack will update shortly.");
        setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to trigger deploy";
        setActionError(errorMessage);
      } finally {
        setActionLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const buildAndDeploy = useCallback(
    async (targetEnvironmentSlug: string) => {
      if (!sysAlias || !targetEnvironmentSlug) return;
      setActionLoading(true);
      setActionError(null);
      setDeployMessage(null);
      try {
        const result = await applicationApi.buildAndDeploy(
          sysAlias,
          targetEnvironmentSlug,
        );
        setDeployMessage(`Build & deploy queued. Job: ${result.jobId}`);
        setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
        // Notify PipelineTab to eagerly select the new run and refetch the runs list
        if (result.jobId) {
          window.dispatchEvent(
            new CustomEvent("rusha:pipeline-run-created", {
              detail: { jobId: result.jobId, systemAlias: sysAlias },
            }),
          );
        }
      } catch (err) {
        // P1-019: the run command returns 409 while a non-terminal run exists.
        // Surface it as a specific, non-failure message.
        if (
          typeof err === "object" &&
          err !== null &&
          "status" in err &&
          (err as { status?: number }).status === 409
        ) {
          setActionError(
            "A run is already in progress for this stack — wait for it to finish or force-fail it.",
          );
        } else {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to trigger build & deploy";
          setActionError(errorMessage);
        }
      } finally {
        setActionLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const retryRun = useCallback(
    async (sagaId: string) => {
      if (!sysAlias) return;
      setActionLoading(true);
      setActionError(null);
      setDeployMessage(null);
      try {
        const result = await applicationApi.retryRun(sysAlias, sagaId);
        setDeployMessage(`Run retry queued. Job: ${result.jobId}`);
        setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
        if (result.jobId) {
          window.dispatchEvent(
            new CustomEvent("rusha:pipeline-run-created", {
              detail: { jobId: result.jobId, systemAlias: sysAlias },
            }),
          );
        }
      } catch (err) {
        if (
          typeof err === "object" &&
          err !== null &&
          "status" in err &&
          (err as { status?: number }).status === 409
        ) {
          setActionError(
            "A run is already in progress for this stack — wait for it to finish or force-fail it.",
          );
        } else {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to retry run";
          setActionError(errorMessage);
        }
      } finally {
        setActionLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const provisionVcs = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    setActionError(null);
    setDeployMessage(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}/provision-vcs`,
        {},
        { ...authHeader() },
      );
      setDeployMessage(
        "VCS provisioning started. This may take a few minutes...",
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to trigger VCS provisioning";
      setActionError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, setSystem]);

  const retryProvisioning = useCallback(async () => {
    if (!sysAlias) return;
    setRetryProvisioningLoading(true);
    setActionError(null);
    try {
      await axios.post(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.RETRY_PROVISIONING.replace(":id", sysAlias)}`,
        {},
        { ...authHeader() },
      );
      setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
    } catch {
      setActionError("Failed to retry VCS provisioning. Please try again.");
    } finally {
      setRetryProvisioningLoading(false);
    }
  }, [sysAlias, setSystem]);

  const softDelete = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.DELETE_APPLICATION.replace(":id", sysAlias)}`,
        { ...authHeader() },
      );
      setDeleteDialogOpen(false);
      if (orgAlias && projectAlias) {
        nav.goToProjectDetails(orgAlias, projectAlias);
      }
    } catch {
      setActionError("Failed to archive stack. Please try again.");
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, orgAlias, projectAlias, nav]);

  const hardDelete = useCallback(async () => {
    if (!sysAlias) return;
    setActionLoading(true);
    try {
      await axios.delete(
        `${apiBaseUrls.BACKEND}${ApplicationEndpoints.DELETE_APPLICATION.replace(":id", sysAlias)}?hard=true`,
        { ...authHeader() },
      );
      setDeleteDialogOpen(false);
      if (orgAlias && projectAlias) {
        nav.goToProjectDetails(orgAlias, projectAlias);
      }
    } catch {
      setActionError("Failed to delete stack. Please try again.");
      setDeleteDialogOpen(false);
    } finally {
      setActionLoading(false);
    }
  }, [sysAlias, orgAlias, projectAlias, nav]);

  const deleteProtectionChange = useCallback(
    async (enabled: boolean) => {
      if (!sysAlias) return;
      setDeleteProtectionLoading(true);
      try {
        await axios.patch(
          `${apiBaseUrls.BACKEND}${ApplicationEndpoints.SET_DELETE_PROTECTION.replace(":id", sysAlias)}`,
          { enabled },
          { ...authHeader() },
        );
        setSystem((s) => (s ? { ...s, deleteProtection: enabled } : s));
      } catch {
        setActionError("Failed to update delete protection.");
      } finally {
        setDeleteProtectionLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const autoDeployChange = useCallback(
    async (enabled: boolean) => {
      if (!sysAlias) return;
      setAutoDeployLoading(true);
      try {
        await axios.patch(
          `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}`,
          { autoDeploy: enabled },
          { ...authHeader() },
        );
        setSystem((s) => (s ? { ...s, autoDeploy: enabled } : s));
      } catch {
        setActionError("Failed to update auto-deploy setting.");
      } finally {
        setAutoDeployLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  const [promoteLoading, setPromoteLoading] = useState(false);
  const promote = useCallback(
    async (targetSlug: string) => {
      if (!sysAlias) return;
      setPromoteLoading(true);
      setActionError(null);
      setDeployMessage(null);
      try {
        const { data } = await axios.post(
          `${apiBaseUrls.BACKEND}/api/v1/applications/${sysAlias}/promote`,
          { targetEnvironmentSlug: targetSlug },
          { ...authHeader() },
        );
        setDeployMessage(
          `Promote to ${data.targetEnvironment ?? targetSlug} queued. Job: ${data.jobId}`,
        );
        setSystem((s) => (s ? { ...s, status: StackStatus.PROVISIONING } : s));
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to promote stack";
        setActionError(errorMessage);
      } finally {
        setPromoteLoading(false);
      }
    },
    [sysAlias, setSystem],
  );

  return {
    actionLoading,
    actionError,
    clearActionError,
    deployMessage,
    clearDeployMessage,

    retryProvisioningLoading,
    deleteProtectionLoading,
    autoDeployLoading,
    promoteLoading,

    deleteDialogOpen,
    setDeleteDialogOpen,

    pause,
    resume,
    deploy,
    buildAndDeploy,
    retryRun,
    provisionVcs,
    retryProvisioning,
    softDelete,
    hardDelete,
    deleteProtectionChange,
    autoDeployChange,
    promote,
  };
}

export default useStackActions;
